import { useState } from 'react';
import { RootState } from 'store';
import toast from 'react-hot-toast';
import { LoaderIcon } from 'lucide-react';
import { DialogTitle } from '@radix-ui/react-dialog';
import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { saveFile, setFileName } from 'store/slices/imageGen';
import {
  ImageGenService,
  ImageToolTypeEnum,
} from 'features/text-to-image-generator/services/image-gen.api';
import { useUpscaleImageContext } from 'features/upscale-image/provider';
import { cn } from 'lib/utils';
import CheckAuthModel from 'features/chat-with-docs/components/CheckAuthModel';
import AuthRequiredModal from 'features/text-to-image-generator/components/modals/AuthRequiredModal';

export default function SaveImageModal({
  generatedImage,
}: {
  generatedImage?: string | null;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const { fileName } = useSelector((state: RootState) => state.imageGen);
  const { imageSaved, setImageSaved } = useUpscaleImageContext();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  const dispatch = useDispatch();

  const saveImage = useMutation({
    mutationKey: ['save-generated-image'],
    mutationFn: ImageGenService.saveImageEditorGeneratedImage,
    onSuccess: () => {
      setIsOpen(false);
      dispatch(saveFile({ fileSaved: true }));
      toast.success(`${fileName} saved `);
    },
  });

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!generatedImage) {
      toast.error('Please select an image to save');
      return;
    }

    const response = await saveImage.mutateAsync({
      image_name: fileName,
      image_url: generatedImage,
      tool_type: ImageToolTypeEnum.UPSCALE,
    });

    if (response) {
      setImageSaved(true);
    }
  };

  return (
    <div>
      {isAuthModalOpen && (
        <AuthRequiredModal
          isOpen={isAuthModalOpen}
          onOpenChange={setIsAuthModalOpen}
          type="save"
        />
      )}
      <Dialog
        open={isOpen}
        onOpenChange={(e) => {
          if (!e) dispatch(setFileName({ fileName: 'Untitled 1' }));

          setIsOpen(e);
        }}
      >
        <DialogTrigger asChild>
          <Button
            variant={'customSecondary'}
            className="bg-secondary max-md:min-w-[180px]"
            size={'sm'}
            onClick={(e) => {
              if (!isAuthenticated) {
                e.preventDefault();
                e.stopPropagation();

                setIsAuthModalOpen(true);
                return;
              }
            }}
          >
            Save
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-text-primary">
              Save as
            </DialogTitle>
          </DialogHeader>
          <form>
            <div className="flex flex-col gap-2">
              <label htmlFor="fileName" className="text-text-primary">
                Image name
              </label>
              <Input
                name="fileName"
                value={fileName}
                className="bg-background-main text-text-primary"
                onChange={(e) => {
                  const { value } = e.target;
                  dispatch(setFileName({ fileName: value }));
                }}
                type="text"
                min={2}
              />
            </div>
            <DialogFooter className="mt-4">
              <Button
                onClick={() => {
                  dispatch(setFileName({ fileName: 'Untitled 1' }));
                  setIsOpen(false);
                }}
                variant={'customSecondary'}
                size={'sm'}
                type="button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className={cn(
                  'text-[12px] font-normal',
                  imageSaved && 'bg-semantics-success text-white'
                )}
                variant={'customPrimary'}
                size={'sm'}
                disabled={saveImage.isPending || imageSaved}
              >
                {saveImage.isPending && (
                  <div className="animate-spin">
                    <LoaderIcon size={20} />
                  </div>
                )}
                {saveImage.isPending ? 'Saving' : 'Save & Continue'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
